import { useAuthStore } from '../auth/useAuthStore'
import { attachPlatform, connectActivePlatform } from './platforms'
import { authedJSON } from './http'
import type {
  NormalizedOrderResult,
  OrderIntent,
  PortfolioResponse,
  QuotesResponse,
  Transaction,
} from './types'

function requireAccountId(): string {
  const accountId = useAuthStore.getState().accountId
  if (!accountId) throw new Error('No active account')
  return accountId
}

export async function connect(
  platformId: string,
  credentials: Record<string, string>,
): Promise<{ accountId: string }> {
  const { setConnection } = useAuthStore.getState()
  const result = await attachPlatform(platformId, credentials)
  if (!result.connection?.accounts?.length || !result.connection.accountId) {
    throw new Error('No accounts returned by the platform')
  }
  setConnection(result.connection)
  return { accountId: result.connection.accountId }
}

export async function connectExistingPlatform(): Promise<{ accountId: string }> {
  const { setConnection } = useAuthStore.getState()
  const connection = await connectActivePlatform()
  if (!connection.accounts?.length || !connection.accountId) {
    throw new Error('No accounts returned by the active platform')
  }
  setConnection(connection)
  return { accountId: connection.accountId }
}

export async function getQuote(symbol: string): Promise<QuotesResponse> {
  const accountId = requireAccountId()
  const params = new URLSearchParams({ symbols: symbol })
  return authedJSON<QuotesResponse>(
    `/api/accounts/${encodeURIComponent(accountId)}/quotes?${params}`,
  )
}

export async function getQuotesBatch(symbols: string[]): Promise<QuotesResponse> {
  if (symbols.length === 0) return { quotes: [] }
  const accountId = requireAccountId()
  const params = new URLSearchParams({ symbols: symbols.join(',') })
  return authedJSON<QuotesResponse>(
    `/api/accounts/${encodeURIComponent(accountId)}/quotes?${params}`,
  )
}

export async function getPortfolio(): Promise<PortfolioResponse> {
  const accountId = requireAccountId()
  return authedJSON<PortfolioResponse>(
    `/api/accounts/${encodeURIComponent(accountId)}/portfolio`,
  )
}

export async function fetchAllTransactions(opts: {
  start?: string
  end?: string
} = {}): Promise<Transaction[]> {
  const accountId = requireAccountId()
  return fetchTransactionsForAccount(accountId, opts)
}

export async function fetchTransactionsForAccount(
  accountId: string,
  opts: { start?: string; end?: string } = {},
): Promise<Transaction[]> {
  const params = new URLSearchParams()
  params.set('accountId', accountId)
  if (opts.start) params.set('start', opts.start)
  if (opts.end) params.set('end', opts.end)
  const res = await authedJSON<{ transactions: Transaction[] }>(
    `/api/transactions?${params}`,
  )
  return res.transactions
}

export async function placeOrder(intent: OrderIntent): Promise<NormalizedOrderResult> {
  const accountId = requireAccountId()
  return authedJSON<NormalizedOrderResult>(
    `/api/accounts/${encodeURIComponent(accountId)}/orders`,
    { method: 'POST', body: JSON.stringify(intent) },
  )
}

export async function placeEquityOrder(params: {
  symbol: string
  side: 'BUY' | 'SELL'
  quantity?: number
  amount?: number
  mode: 'MARKET' | 'LIMIT'
  limitPrice?: number
  extendedHours?: boolean
}): Promise<NormalizedOrderResult> {
  if (params.quantity == null && params.amount == null) {
    throw new Error('placeEquityOrder requires quantity or amount')
  }
  if (params.mode === 'LIMIT' && params.limitPrice == null) {
    throw new Error('placeEquityOrder requires limitPrice when mode is LIMIT')
  }
  if (params.extendedHours && params.mode !== 'LIMIT') {
    throw new Error('extended-hours orders must be LIMIT')
  }
  return placeOrder({
    symbol: params.symbol,
    instrumentType: 'EQUITY',
    side: params.side,
    type: params.mode,
    timeInForce: 'DAY',
    ...(params.quantity != null ? { quantity: params.quantity } : { amount: params.amount }),
    ...(params.mode === 'LIMIT' ? { limitPrice: params.limitPrice } : {}),
    ...(params.extendedHours ? { extendedHours: true } : {}),
  })
}

export async function cancelOrder(orderId: string): Promise<{ orderId: string; status: string }> {
  const accountId = requireAccountId()
  return authedJSON<{ orderId: string; status: string }>(
    `/api/accounts/${encodeURIComponent(accountId)}/orders/${encodeURIComponent(orderId)}`,
    { method: 'DELETE' },
  )
}
